import React, { useState, useEffect, createContext, useContext, useCallback, useMemo } from 'react';
import { useSyncExternalStore } from 'react';
import { logger } from '@repo/shared-utils';
import { writeHistory } from './navigation/writeHistory';
import { getRouterSearchSnapshot, subscribeRouter } from './navigation/routerStore';

// --- CUSTOM ROUTER IMPLEMENTATION ---

interface RouterContextValue {
  path: string;
  navigate: (to: string | number, options?: { replace?: boolean; state?: unknown }) => void;
  params: Record<string, string | undefined>;
  routeContent: React.ReactNode | null;
}

export const RouterContext = createContext<RouterContextValue | null>(null);

export const useRouter = () => {
  const context = useContext(RouterContext);
  if (!context) {
    // Fallback for when hook is used outside of AppRouter (should be avoided but prevents crash)
    logger.error('useRouter must be used within AppRouter', { module: 'routes', action: 'use_router_outside_context' });
    return {
      path: window.location.pathname,
      navigate: (to: string | number) => {
        if (typeof to === 'number') {
          window.history.go(to);
        } else {
          window.location.href = to;
        }
      },
      params: {},
      routeContent: null
    };
  }
  return context;
};

export const useNavigate = () => {
  const { navigate } = useRouter();
  return navigate;
};

export const useLocation = () => {
  const { path } = useRouter();
  const search = useSyncExternalStore(
    subscribeRouter,
    getRouterSearchSnapshot,
    () => '',
  );
  return { pathname: path, search };
};

export const useParams = <T extends Record<string, string | undefined> = Record<string, never>>(): T => {
  const { params } = useRouter();
  return (params || {}) as T;
};

export const useSearchParams = (): [URLSearchParams, (newParams: URLSearchParams | Record<string, string> | string) => void] => {
  const search = useSyncExternalStore(
    subscribeRouter,
    getRouterSearchSnapshot,
    () => '',
  );

  const searchParams = useMemo(() => {
    const raw = search.startsWith('?') ? search.slice(1) : search;
    return new URLSearchParams(raw);
  }, [search]);

  const setParams = useCallback((newParams: URLSearchParams | Record<string, string> | string) => {
    const qs =
      typeof newParams === 'string'
        ? newParams.replace(/^\?/, '')
        : newParams instanceof URLSearchParams
          ? newParams.toString()
          : new URLSearchParams(newParams).toString();
    const pathname = typeof window === 'undefined' ? '/' : window.location.pathname;
    const newUrl = `${pathname}${qs ? `?${qs}` : ''}`;
    try {
      writeHistory(() => {
        window.history.pushState(null, '', newUrl);
      });
    } catch (_e) {
      logger.warn('History pushState failed (likely sandboxed).', { module: 'routes', action: 'set_search_params' });
    }
  }, []);

  return [searchParams, setParams];
};

export const useBlocker = () => {
  return { state: "unblocked", proceed: () => {}, reset: () => {} };
};

export const Navigate = ({ to, replace }: { to: string, replace?: boolean }) => {
  const { navigate } = useRouter();
  
  useEffect(() => {
    navigate(to, { replace });
  }, [to, navigate, replace]);
  return null;
};

export const Outlet = () => {
  const { routeContent } = useRouter();
  return routeContent;
};

/**
 * Link component compatible with the custom router.
 * Provides the same API as react-router-dom's Link but works with the custom router context.
 */
export const Link = ({ to, children, className, replace, ...props }: { 
  to: string; 
  children: React.ReactNode; 
  className?: string;
  replace?: boolean;
  [key: string]: unknown;
}) => {
  const { navigate } = useRouter();
  
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    navigate(to, { replace });
  };
  
  return (
    <a 
      href={to} 
      onClick={handleClick}
      className={className}
      {...props}
    >
      {children}
    </a>
  );
};

// --- ROUTE MATCHING UTILS ---

export const matchPath = (pattern: string, path: string) => {
  const patternParts = pattern.split('/').filter(Boolean);
  const pathParts = path.split('/').filter(Boolean);
  
  if (patternParts.length > pathParts.length) return null;
  
  const params: Record<string, string> = {};
  
  for (let i = 0; i < patternParts.length; i++) {
    const patternPart = patternParts[i];
    const pathPart = pathParts[i];
    
    if (patternPart.startsWith(':')) {
      params[patternPart.slice(1)] = pathPart;
    } else if (patternPart !== pathPart) {
      return null;
    }
  }
  
  return { params, isExact: patternParts.length === pathParts.length };
};
